/**
 * Servicio de configuración centralizada
 * Basado en JsonRequest.cs
 */

import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

export interface Config {
  baseUrl: string;
  baseUrlLLM: string;
  client: string;
  assistantID: string;
  testingPhase: boolean;
  testingWebSocket: boolean;
  accessToken?: string;
  refreshToken?: string;
  email?: string;
  userFullName?: string;
  userFirstname?: string;
  userLastname?: string;
  userID?: number;
  promptReference?: string;
  obtainFullPromptURL?: string;
  promptAccessToken?: string;
  promptRefreshToken?: string;
  fullPrompt?: string;
  practiceMode?: string; // Modo de práctica (conversacional, estudio, examen, etc.)
  accent?: string; // Acento seleccionado (Francia, Canadá, Australia, Quebecois)
  voice?: string;
  language?: string;
  character?: string;
  summaryPrevAssistant?: string; // Resumen de la última interacción del estudiante
}

class ConfigService {
  private config: Config = {
    baseUrl: '',
    baseUrlLLM: '',
    client: 'alianzafrancesa',
    assistantID: '',
    testingPhase: true,
    testingWebSocket: true,
  };

  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      timeout: 30000,
    });

    // Interceptor para agregar token Bearer
    this.axiosInstance.interceptors.request.use(
      (config) => {
        if (this.config.accessToken) {
          config.headers.Authorization = `Bearer ${this.config.accessToken.trim()}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Interceptor para manejar errores 401 (token expirado)
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && this.config.refreshToken) {
          // Intentar refrescar el token
          try {
            await this.refreshToken();
            // Reintentar la petición original
            return this.axiosInstance.request(error.config);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  initialize(config: Partial<Config>): void {
    this.config = { ...this.config, ...config };
    
    // Construir URLs
    if (this.config.testingPhase) {
      // En local/desarrollo, siempre usar servidor de desarrollo
      this.config.baseUrl = import.meta.env.VITE_BASE_URL_DEV || 'https://learnenglish.niimx.us/';
      this.config.baseUrlLLM = import.meta.env.VITE_LLM_BASE_URL_DEV || 'https://llm-dev.aitopstaff.com';
    } else {
      // En producción, usar servidor de producción
      // baseUrl ya no se usa - todas las llamadas se hacen a través de baseUrlLLM
      this.config.baseUrl = import.meta.env.VITE_BASE_URL_PROD || 'https://learnenglish.mentoraibot.com/';
      this.config.baseUrlLLM = import.meta.env.VITE_LLM_BASE_URL_PROD || 'https://llm.aitopstaff.com';
    }

    this.config.obtainFullPromptURL = `${this.config.baseUrlLLM}/api/v1/bot/get_full_prompt/`;
  }

  getConfig(): Config {
    return { ...this.config };
  }

  setAccessToken(token: string): void {
    this.config.accessToken = token;
  }

  setRefreshToken(token: string): void {
    this.config.refreshToken = token;
  }

  setPromptAccessToken(token: string): void {
    this.config.promptAccessToken = token;
  }

  setPromptRefreshToken(token: string): void {
    this.config.promptRefreshToken = token;
  }

  setUserInfo(email: string, fullName: string, userID?: number, firstname?: string, lastname?: string): void {
    this.config.email = email;
    this.config.userFullName = fullName;
    this.config.userFirstname = firstname;
    this.config.userLastname = lastname;
    if (userID) {
      this.config.userID = userID;
    }
  }

  /**
   * Actualizar promptReference dinámicamente
   */
  setPromptReference(promptReference: string): void {
    this.config.promptReference = promptReference;
    // Limpiar el prompt completo para forzar recarga con nuevo reference
    this.config.fullPrompt = undefined;
  }

  /**
   * Actualizar avatarType/mode para el avatar
   */
  setAvatarType(avatarType: string): void {
    // Guardar en una propiedad adicional si es necesario
    (this.config as any).avatarType = avatarType;
  }

  getAvatarType(): string | undefined {
    return (this.config as any).avatarType;
  }

  /**
   * Guardar modo de práctica y acento para configuración de voz
   */
  setPracticeModeAndAccent(mode: string, accent: string): void {
    this.config.practiceMode = mode;
    this.config.accent = accent;
  }

  setVoiceSelection(voice: string, language: string, character: string): void {
    this.config.voice = voice;
    this.config.language = language;
    this.config.character = character;
  }

  getVoiceSelection(): { voice?: string; language?: string; character?: string } {
    return {
      voice: this.config.voice,
      language: this.config.language,
      character: this.config.character,
    };
  }

  getPracticeMode(): string | undefined {
    return this.config.practiceMode;
  }

  getAccent(): string | undefined {
    return this.config.accent;
  }

  setSummaryPrevAssistant(summary: string): void {
    this.config.summaryPrevAssistant = summary;
  }

  getSummaryPrevAssistant(): string {
    return this.config.summaryPrevAssistant || '';
  }

  /**
   * Obtener el resumen de la última interacción del estudiante desde el dashboard API
   * Basado en GetQuestionsUrl del proyecto Unity (JsonRequest.cs línea 903-961)
   */
  async fetchPreviousSummary(email: string): Promise<void> {
    try {
      const questionsUrl = `${this.config.baseUrl}api/v1/mentor/questions/?student_email=${encodeURIComponent(email)}`;
      // console.log('[ConfigService] Consultando resumen previo:', questionsUrl);

      const response = await this.axiosInstance.get<{
        Count: number;
        Results: Array<{
          summary?: string;
          insights?: string;
          mood?: string;
          reminder?: string;
        }>;
      }>(questionsUrl);

      const data = response.data;

      if (data && data.Count > 0 && data.Results && data.Results[0]) {
        const lastInteraction = data.Results[0];

        if (lastInteraction.summary) {
          this.config.summaryPrevAssistant = lastInteraction.summary;
          // console.log('[ConfigService] ✅ Resumen previo cargado:', lastInteraction.summary.substring(0, 100) + '...');
        } else {
          // console.log('[ConfigService] La última interacción no tiene resumen.');
          this.config.summaryPrevAssistant = '';
        }
      } else {
        // console.log('[ConfigService] No se encontraron interacciones previas para:', email);
        this.config.summaryPrevAssistant = '';
      }
    } catch (error) {
      // console.warn('[ConfigService] ⚠️ Error al obtener resumen previo (no crítico):', error);
      this.config.summaryPrevAssistant = '';
    }
  }

  // Métodos HTTP helpers
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data, {
      ...config,
      headers: {
        'Content-Type': 'application/json',
        ...config?.headers,
      },
    });
    return response.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.patch<T>(url, data, {
      ...config,
      headers: {
        'Content-Type': 'application/json',
        ...config?.headers,
      },
    });
    return response.data;
  }

  async getWithBearerToken<T>(url: string, token: string): Promise<T> {
    const response = await axios.get<T>(url, {
      headers: {
        Authorization: `Bearer ${token.trim()}`,
      },
    });
    return response.data;
  }

  async postWithBearerToken<T>(url: string, token: string, data: any): Promise<T> {
    const response = await axios.post<T>(url, data, {
      headers: {
        Authorization: `Bearer ${token.trim()}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  async patchWithBearerToken<T>(url: string, token: string, data: any): Promise<T> {
    const response = await axios.patch<T>(url, data, {
      headers: {
        Authorization: `Bearer ${token.trim()}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  }

  private async refreshToken(): Promise<void> {
    if (!this.config.refreshToken || !this.config.baseUrlLLM) {
      throw new Error('No hay refresh token disponible');
    }

    // Usar baseUrlLLM en lugar de baseUrl (ya no se usa mentoraibot.com)
    const refreshUrl = `${this.config.baseUrlLLM}/api/v1/auth/refresh/`;
    const response = await axios.post<{ access: string }>(refreshUrl, {
      refresh: this.config.refreshToken,
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.config.accessToken = response.data.access;
  }

  /**
   * Login para obtener token de acceso al prompt
   * Basado en promptApiConection() del código C# original
   */
  async loginForPrompt(): Promise<void> {
    if (this.config.promptAccessToken) {
      // Ya tenemos el token, no es necesario hacer login de nuevo
      return;
    }

    const loginUrl = `${this.config.baseUrlLLM}/api/v1/login/`;
    
    const response = await axios.post<{
      tokens: {
        access: string;
        refresh: string;
      };
    }>(loginUrl, {
      username: 'llmprompts',
      password: 'Z-:sMOL6L_',
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.config.promptAccessToken = response.data.tokens.access;
    this.config.promptRefreshToken = response.data.tokens.refresh;
  }

  // IMPORTANTE: Llamado al prompt principal
  async getFullPrompt(promptReference: string, context: any): Promise<string> {
    if (!this.config.obtainFullPromptURL || !this.config.promptAccessToken) {
      throw new Error('Configuración de prompt no disponible');
    }

    const requestData = {
      ref: promptReference,
      context,
    };


    try {
      const response = await this.postWithBearerToken<{ payload: string }>(
        this.config.obtainFullPromptURL,
        this.config.promptAccessToken,
        requestData
      );

      this.config.fullPrompt = response.payload;
      return response.payload;
    } catch (error: any) {
      // Mejorar mensaje de error para errores 500 (prompt no encontrado)
      if (error.response?.status === 500) {
        // console.error(`[ConfigService] Error 500 al obtener prompt: ${promptReference}`);
        // console.error('[ConfigService] El prompt reference no existe en el backend:', promptReference);
        throw new Error(`El prompt "${promptReference}" no existe en el backend. Por favor verifica que el prompt esté configurado correctamente.`);
      }
      // Re-lanzar otros errores
      throw error;
    }
  }

  /**
   * Actualizar el prompt completo después de obtenerlo
   */
  setFullPrompt(fullPrompt: string): void {
    this.config.fullPrompt = fullPrompt;
  }
}

export const configService = new ConfigService();
export default configService;


